/**
 * @file view-customers.tsx
 * @module app/modules/customers/view-customers
 * @description Displays the general customer list with role-aware actions, filtering, reporting,
 * pagination, and navigation to customer profiles and related workflows.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

import { useEffect, useState } from 'react';
import {
  Button,
  Card,
  Col,
  OverlayTrigger,
  Popover,
  Tooltip,
  Row,
  FormLabel,
} from 'react-bootstrap';
import APICallService from '../../../api/apiCallService';
import { useNavigate, useLocation } from 'react-router';
import { Link } from 'react-router-dom';
import Loader from '../../../Global/loader';
import Pagination from '../../../Global/pagination';
import b1 from '../../../_admin/assets/media/product/default.svg';
import { KTSVG } from '../../../_admin/helpers';
import {
  buyer,
  customerService,
  master,
  multipleWarehouse,
  reports,
} from '../../../api/apiEndPoints';
import { IGetCustomers } from '../../../types/responseIndex';
import Method from '../../../utils/methods';
import { useAuth } from '../auth';
import {
  Add,
  Admin,
  AllCustomers,
  AllModules,
  AssistAdmin,
  Edit,
  FinanceManager,
  FulfillmentManager,
  NotRegistered,
  PAGE_LIMIT,
  View,
} from '../../../utils/constants';
import {
  listBuyerOrder,
  listCustomerProfile,
  listCustomerRefundDetails,
  listCustomers,
  warehouse as warehouseStoreString,
} from '../../../utils/storeString';
import {
  getKey,
  removeAllKeys,
  removeKey,
  setKey,
} from '../../../Global/history';
import AllCustomerReport from '../../modals/report/all-customers-report';
import { CustomSelect } from '../../custom/Select/CustomSelect';
import errorDanger from '../../../_admin/assets/media/svg_uTrade/error-danger.svg';
import { customerStateJSON } from '../../../utils/staticJSON';
import { ElementAnimateUtil } from '../../../_admin/assets/ts/_utils';
import { error } from '../../../Global/toast';
import CustomDatePicker from '../../custom/DateRange/DatePicker';
import { useWarehouse } from '../../context/WarehouseContext';
import moment from 'moment';
import { useDebounce } from '../../../utils/useDebounce';
import { useNavigationHistory } from '../../../utils/useNavigationHistory';
import { svg } from '../../../utils/svg';
import ImageWithFallback from '../_common/components/image-with-fallback';

const customerTypeOptions = [
  { label: 'Pharmacy', value: 'true' },
  { label: 'Utrade', value: 'false' },
];

const ViewCustomers = () => {
  const navigate = useNavigate();
  const { navigateTo } = useNavigationHistory();
  const { currentUser }: any = useAuth();
  const { selectedWarehouse } = useWarehouse();
  const [fetchLoading, setFetchLoading] = useState(true);
  const [fetchTableLoading, setFetchTableLoading] = useState(true);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState<any>(getKey(listCustomers.page) || 1);
  const [pageLimit, setPageLimit] = useState<any>(
    getKey(listCustomers.limit) || PAGE_LIMIT
  );
  const [fetchLoader, setFetchLoader] = useState(false);
  const [customers, setCustomers] = useState<IGetCustomers | any>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [dataFetched, setDataFetched] = useState(false);
  const [search, setSearch] = useState<string>(
    getKey(listCustomers.search)?.toString() || ''
  );
  const [empty, setEmpty] = useState(false);
  const [showDownload, setShowDownload] = useState(false);
  const [registrationLoading, setRegistrationLoading] = useState(false);
  const [customerState, setCustomerState] = useState<any>(
    getKey(listCustomers.filterCustomerState)
  );
  const [districts, setDistricts] = useState([]);
  const [totalDistrict, setTotalDistrict] = useState(0);
  const [selectedDistrict, setSelectedDistrict] = useState<any>(null);
  const [businessTypes, setBusinessTypes] = useState([]);
  const [selectedBusinessTypes, setSelectedBusinessTypes] = useState<any>(null);
  const [selectedCustomerType, setSelectedCustomerType] = useState<any>(null);
  const [startDate, setStartDate] = useState<any>(null);
  const [endDate, setEndDate] = useState<any>(null);

  // Helper function to format date for API
  // Takes the date selected by user (local timezone) and formats it as YYYY-MM-DD
  // This preserves the calendar date selected by the user
  const convertToUTC = (date: Date) => {
    // Format the date directly without timezone conversion
    // If user selects Nov 5, 2024, API receives "2024-11-05"
    return moment(date).format('YYYY-MM-DD');
  };
  useEffect(() => {
    (async () => {
      setFetchLoading(true);
      if (!Method.hasModulePermission(AllCustomers, currentUser)) {
        return window.history.back();
      }
      const warehouseValue = selectedWarehouse?.value || null;
      await fetchInquiryData(
        page,
        pageLimit,
        search,
        true,
        customerState,
        selectedDistrict,
        selectedBusinessTypes,
        warehouseValue || null
      );
      await fetchDistricts();
      await fetchBusinessTypes();
      setDataFetched(true);
      setFetchLoading(false);
    })();
  }, [selectedWarehouse]);
  useEffect(() => {
    if (dataFetched) {
      let initialScrollPosition: any = getKey(listCustomers.scrollPosition);
      if (initialScrollPosition) {
        ElementAnimateUtil.scrollTo(parseInt(initialScrollPosition));
        setDataFetched(false);
        setKey(listCustomers.search, '', false);
        setKey(listCustomers.filterCustomerState, null, false);
      }
      removeKey(listCustomerProfile.currentTab);
      removeAllKeys(listBuyerOrder);
      removeKey(listCustomerRefundDetails.filter);
    }
  }, [dataFetched]);
  const fetchDistricts = async () => {
    setLoading(true);
    let params = {};
    let apiService = new APICallService(
      customerService.listDistrict,
      params,
      '',
      '',
      '',
      '',
      AllCustomers
    );
    let response = await apiService.callAPI();
    setTotalDistrict(response.total);
    setDistricts(response.records);
    setLoading(false);
  };
  const fetchBusinessTypes = async () => {
    let apiService = new APICallService(
      master.listBusinessTypes,
      {
        'addedFor[1]': 2,
      },
      '',
      '',
      '',
      '',
      AllCustomers
    );
    let response = await apiService.callAPI();
    if (response) {
      setBusinessTypes(response.records);
    }
  };
  const fetchInquiryData = async (
    pageNo: number,
    limit: number,
    search: string,
    initial?: boolean,
    state?: number,
    district?: any,
    type?: any,
    warehouse?: any,
    fromDate?: any,
    toDate?: any,
    customerType?: any
  ) => {
    setFetchTableLoading(true);
    let params: any = {
      pageNo: pageNo,
      limit: limit,
      sortKey: 'createdAt',
      sortOrder: -1,
      searchTerm: search,
      needCount: true,
      // state: 1,
    };
    if (state !== -1) {
      params = {
        ...params,
        state: state,
      };
    }
    console.log('district,type', district, type);
    if (district) {
      params = {
        ...params,
        ['districts[0]']: district,
      };
    }
    if (type) {
      params = {
        ...params,
        ['types[0]']: type,
      };
    }
    const customerTypeFilter =
      customerType !== undefined ? customerType : selectedCustomerType;
    if (customerTypeFilter !== null && customerTypeFilter !== undefined) {
      params = {
        ...params,
        isPharmacyVendor: customerTypeFilter,
      };
    }
    if (warehouse) {
      params = {
        ...params,
        warehouse: warehouse,
      };
    }
    if (fromDate && toDate) {
      params = {
        ...params,
        fromDate: convertToUTC(fromDate),
        toDate: convertToUTC(toDate),
      };
    }
    let apiService = new APICallService(
      buyer.listBuyer,
      params,
      '',
      '',
      '',
      '',
      AllCustomers,
      warehouse
    );
    let response = await apiService.callAPI();
    if (response) {
      if (response.total) {
        setTotalRecords(response.total);
      }
      if (initial && !search) {
        if (response.records.length) {
          setEmpty(false);
        } else {
          setEmpty(true);
        }
      } else {
        setEmpty(false);
      }
      setCustomers(response.records);
    }
    setFetchTableLoading(false);
  };
  const handleCurrentPage = async (val: number) => {
    setPage(val);
    setKey(listCustomers.page, val);
    setLoading(true);
    await fetchInquiryData(
      val,
      pageLimit,
      search,
      false,
      customerState,
      selectedDistrict,
      selectedBusinessTypes,
      selectedWarehouse?.value || null,
      startDate,
      endDate
    );
    setLoading(false);
  };
  const handleNextPage = async (val: number) => {
    setPage(val + 1);
    setKey(listCustomers.page, val + 1);
    setLoading(true);
    await fetchInquiryData(
      val + 1,
      pageLimit,
      search,
      false,
      customerState,
      selectedDistrict,
      selectedBusinessTypes,
      selectedWarehouse?.value || null,
      startDate,
      endDate
    );
    setLoading(false);
  };
  const handlePreviousPage = async (val: number) => {
    setLoading(true);
    setPage(val - 1);
    setKey(listCustomers.page, val - 1);
    await fetchInquiryData(
      val - 1,
      pageLimit,
      search,
      false,
      customerState,
      selectedDistrict,
      selectedBusinessTypes,
      selectedWarehouse?.value || null,
      startDate,
      endDate
    );
    setLoading(false);
  };
  const handlePageLimit = async (event: any) => {
    setLoading(true);
    setPage(1);
    setKey(listCustomers.page, 1);
    setKey(listCustomers.limit, parseInt(event.target.value));
    await setPageLimit(parseInt(event.target.value));
    await fetchInquiryData(
      1,
      event.target.value,
      search,
      false,
      customerState,
      selectedDistrict,
      selectedBusinessTypes,
      selectedWarehouse?.value || null,
      startDate,
      endDate
    );
    setLoading(false);
  };

  const debounce = useDebounce(fetchInquiryData, 500);
  const handleSearch = async (value: string) => {
    setLoading(true);
    setSearch(value);
    setKey(listCustomers.search, value, false);
    setKey(listCustomers.page, 1);
    await debounce(
      1,
      pageLimit,
      value,
      false,
      customerState,
      selectedDistrict,
      selectedBusinessTypes,
      selectedWarehouse?.value || null,
      startDate,
      endDate
    );
    if (!value.length) {
      setPage(1);
    }
    setLoading(false);
  };
  const handleCustomerProfile = (id: string) => {
    let currentScrollY = window.scrollY;
    localStorage.setItem('CustomerId', id);
    setKey(listCustomers.scrollPosition, currentScrollY);
    setKey(listCustomers.search, search, false);
    setKey(listCustomers.filterCustomerState, customerState, false);
    navigateTo('/customers/customer-profile', { state: id });
  };
  const handleFilter = async (value: number) => {
    setCustomerState(value);
    setKey(listCustomers.filterCustomerState, value, false);
    setKey(listCustomers.page, 1);
    setLoading(true);
    setPage(1);
    await fetchInquiryData(
      1,
      pageLimit,
      search,
      false,
      value,
      selectedDistrict,
      selectedBusinessTypes,
      selectedWarehouse?.value || null,
      startDate,
      endDate
    );
    setLoading(false);
  };

  const popover = (
    <Popover id="popover-basic" className="bg-black">
      <Popover.Body className="p-2 bg-black border-r10px text-white">
        <span>Customer haven't completed registration</span>
      </Popover.Body>
    </Popover>
  );
  const handleStatus = async (id: string, status: boolean, index: number) => {
    let temp: any = [...customers];
    let apiService = new APICallService(
      buyer.blockBuyer,
      { blocked: !status },
      {
        id: id,
      },
      '',
      '',
      '',
      AllCustomers
    );
    let response = await apiService.callAPI();
    if (response) {
      temp[index].blocked = !status;
      
      // await fetchInquiryData(
      //   1,
      //   pageLimit,
      //   search,
      //   false,
      //   customerState,
      //   selectedDistrict,
      //   selectedBusinessTypes,
      //   startDate,
      //   endDate
      // );
    }
    setCustomers(temp);
  };
  const handleDownload = async (
    pageNo: number,
    limit: number,
    search: string,
    initial?: boolean,
    state?: number,
    district?: any,
    type?: any,
    fromDate?: any,
    toDate?: any
  ) => {
    setRegistrationLoading(true);
    // let params : any =    {
    //   utcOffset: new Date().getTimezoneOffset(),
    //   state: 0,
    // }
    // if(startDate && endDate){
    //   params = {
    //     ...params,
    //     fromDate:Method.convertDateToFormat(startDate, "YYYY-MM-DD"),
    //     toDate: Method.convertDateToFormat(endDate, "YYYY-MM-DD"),
    //   }
    // }
    let params: any = {
      sortKey: 'createdAt',
      sortOrder: -1,
      searchTerm: search,
      needCount: true,
      // state: 1,
    };
    if (state !== -1) {
      params = {
        ...params,
        state: state,
      };
    }
    console.log('district,type', district, type);
    if (district) {
      params = {
        ...params,
        ['districts[0]']: district,
      };
    }
    if (type) {
      params = {
        ...params,
        ['types[0]']: type,
      };
    }
    if (selectedCustomerType !== null && selectedCustomerType !== undefined) {
      params = {
        ...params,
        isPharmacyVendor: selectedCustomerType,
      };
    }
    if (fromDate && toDate) {
      params = {
        ...params,
        fromDate: convertToUTC(fromDate),
        toDate: convertToUTC(toDate),
      };
    }
    let apiService = new APICallService(
      reports.customerRegistrationReport,
      params,
      undefined,
      'blob',
      '',
      '',
      AllCustomers,
      selectedWarehouse?.value || null
    );
    let response = await apiService.callAPI();
    if (response) {
      const pdfBlob = new Blob([response], { type: 'application/pdf' });
      const downloadLink = document.createElement('a');
      downloadLink.href = URL.createObjectURL(pdfBlob);
      downloadLink.download = 'registration_report' + '.pdf';
      downloadLink.click();
    } else {
      error('No data found');
    }
    setRegistrationLoading(false);
  };
  const handleDistrict = async (event: any) => {
    console.log(event);
    if (event) {
      setSelectedDistrict(event);
      await fetchInquiryData(
        1,
        pageLimit,
        search,
        false,
        customerState,
        event,
        selectedBusinessTypes,
        selectedWarehouse?.value || null,
        startDate,
        endDate
      );
    } else {
      setSelectedDistrict(null);
      await fetchInquiryData(
        1,
        pageLimit,
        search,
        false,
        customerState,
        null,
        selectedBusinessTypes,
        selectedWarehouse?.value || null,
        startDate,
        endDate
      );
    }
  };
  const handleBusinessTypesSelect = async (event: any) => {
    if (event) {
      setSelectedBusinessTypes(event);
      await fetchInquiryData(
        1,
        pageLimit,
        search,
        false,
        customerState,
        selectedDistrict,
        event,
        selectedWarehouse?.value || null,
        startDate,
        endDate
      );
    } else {
      setSelectedBusinessTypes(null);
      await fetchInquiryData(
        1,
        pageLimit,
        search,
        false,
        customerState,
        selectedDistrict,
        null,
        selectedWarehouse?.value || null,
        startDate,
        endDate
      );
    }
  };

  const handleCustomerTypeSelect = async (event: any) => {
    const selectedValue = event
      ? event.value === true || event.value === 'true'
        ? true
        : event.value === false || event.value === 'false'
        ? false
        : null
      : null;
    setSelectedCustomerType(selectedValue);
    setPage(1);
    setKey(listCustomers.page, 1);
    setLoading(true);
    await fetchInquiryData(
      1,
      pageLimit,
      search,
      false,
      customerState,
      selectedDistrict,
      selectedBusinessTypes,
      selectedWarehouse?.value || null,
      startDate,
      endDate,
      selectedValue
    );
    setLoading(false);
  };

  const handleDateChange = async ([startDate, endDate]: any) => {
    setStartDate(startDate);
    setEndDate(endDate);
    setKey(listCustomers.page, 1);
    setPage(1);
    if (startDate && endDate) {
      setLoading(true);
      setTotalRecords(0);
      await fetchInquiryData(
        1,
        pageLimit,
        search,
        false,
        customerState,
        selectedDistrict,
        selectedBusinessTypes,
        selectedWarehouse?.value,
        startDate,
        endDate
      );
      setLoading(false);
    }
    if (!startDate && !endDate) {
      setLoading(true);
      setTotalRecords(0);
      await fetchInquiryData(
        1,
        pageLimit,
        search,
        false,
        customerState,
        selectedDistrict,
        selectedBusinessTypes,
        selectedWarehouse?.value || null
      );
      setLoading(false);
    }
  };

  return (
    <>
      <div className="p-5">
        <>
        <span className="release-highlighter--customers-header"></span>
        </>
        {showDownload ? (
          <AllCustomerReport
            show={showDownload}
            onHide={() => setShowDownload(false)}
            moduleName={AllCustomers}
          />
        ) : (
          <></>
        )}
        <Row className="align-items-center mb-7">
          <Col sm>
            <h1 className="fs-22 fw-bolder mb-sm-0 mb-3">Customers</h1>
          </Col>
          {!fetchLoading ? (
            <>
              {!empty ? (
                <>
                  {currentUser?.role.some(
                    (obj: any) =>
                      obj.type === FinanceManager ||
                      obj.type === FulfillmentManager
                  ) ? (
                    <></>
                  ) : (
                    <>
                      {Method.hasPermission(AllCustomers, Add, currentUser) ? (
                        <Col sm={'auto'}>
                          <Link
                            to="/customers/add-new-customers"
                            data-analytics-event="customers_view_customers_add_new_customer_link_clicked"
                          >
                            {' '}
                            <Button
                              className="uw-btn uw-btn-primary"
                              data-analytics-event="customers_view_customers_add_new_customer_clicked"
                              onClick={() => {
                                setKey(listCustomers.search, search, false);
                                setKey(
                                  listCustomers.filterCustomerState,
                                  customerState,
                                  false
                                );
                              }}
                            >
                              Add new customer{' '}
                            </Button>
                          </Link>
                        </Col>
                      ) : (
                        <></>
                      )}
                    </>
                  )}
                  {currentUser?.role.length === 0 ||
                  currentUser?.role.some(
                    (obj: any) => obj.type === FinanceManager
                  ) ? (
                    <Col sm="auto">
                      <Button
                        className="uw-btn uw-btn-primary"
                        data-analytics-event="customers_view_customers_download_report_clicked"
                        onClick={() =>
                          handleDownload(
                            1,
                            pageLimit,
                            search,
                            false,
                            customerState,
                            selectedDistrict,
                            selectedBusinessTypes,
                            startDate,
                            endDate
                          )
                        }
                      >
                        {!registrationLoading && (
                          <span className="indicator-label">
                            Download report
                          </span>
                        )}
                        {registrationLoading && (
                          <span
                            className="indicator-progress"
                            style={{ display: 'block' }}
                          >
                            Please wait...
                            <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                          </span>
                        )}
                      </Button>
                    </Col>
                  ) : (
                    <></>
                  )}
                  {currentUser?.role.length === 0 ||
                  currentUser?.role.some(
                    (obj: any) => obj.type === FinanceManager
                  ) ? (
                    <Col sm="auto">
                      <Button
                        className="uw-btn uw-btn-primary"
                        data-analytics-event="customers_view_customers_download_customer_report_clicked"
                        onClick={() => setShowDownload(true)}
                      >
                        Download customer report
                      </Button>
                    </Col>
                  ) : (
                    <></>
                  )}
                </>
              ) : empty ? (
                <>
                  {Method.hasPermission(AllCustomers, Add, currentUser) ? (
                    <Col lg={12} className="mt-6">
                      <Card className="border py-1">
                        <Card.Body>
                          <h2 className="fs-22 fw-bolder">
                            Start adding customers!
                          </h2>
                          <p className="fs-18 fw-500">
                            You can add customers and manage them.
                          </p>
                          {currentUser?.role.some(
                            (obj: any) => obj.type === FinanceManager
                          ) ? (
                            <></>
                          ) : (
                            <>
                              <Link
                                to="/customers/add-new-customers"
                                data-analytics-event="customers_view_customers_empty_state_add_link_clicked"
                              >
                                <Button
                                  className="uw-btn uw-btn-primary uw-btn-lg mt-4"
                                  variant="primary"
                                  size="lg"
                                  data-analytics-event="customers_view_customers_empty_state_add_clicked"
                                >
                                  Add customers
                                </Button>
                              </Link>
                            </>
                          )}
                        </Card.Body>
                      </Card>
                    </Col>
                  ) : (
                    <></>
                  )}
                </>
              ) : (
                <></>
              )}
            </>
          ) : (
            <></>
          )}
        </Row>
        <Row>
          <Col lg={12}>
            {!fetchLoading ? (
              <>
                {!empty ? (
                  <>
                    <Card className="border bg-light mb-4">
                      <Card.Body>
                        <Col className="d-flex justify-content-between uw-common-p-5">
                          <div className="position-relative my-1 col-w-100">
                            <KTSVG
                              path="/media/icons/duotune/general/gen021.svg"
                              className="svg-icon-3 svg-icon-gray-500 position-absolute top-50 translate-middle ps-13"
                            />
                            <input
                              type="text"
                              className="form-control form-control-custom borde-r8px bg-white ps-11"
                              name="Search Team"
                              value={search}
                              onChange={(event) => {
                                handleSearch(event.target.value.trimStart());
                              }}
                              placeholder="Search by customer name, business name"
                            />
                          </div>
                        </Col>

                        <Col className="d-flex justify-content-between flex-wrap uw-common-p-5">
                          <div className="d-flex align-items-center justify-content-between col-w-50 uw-common-p-5">
                            <label htmlFor="" className="fs-16 fw-500">
                              Filter by Business
                            </label>
                            <div className="col-w-60">
                              <CustomSelect
                                backgroundColor="#ffff"
                                minHeight="40px"
                                isLoading={fetchLoader}
                                onChange={(event: any) => {
                                  if (event) {
                                    handleBusinessTypesSelect(event._id);
                                  } else {
                                    handleBusinessTypesSelect(null);
                                  }
                                }}
                                isClearable={true}
                                loading={fetchLoading}
                                isDisabled={fetchLoading}
                                options={
                                  businessTypes.length
                                    ? businessTypes.map((catval: any) => ({
                                        value: catval.title,
                                        label: catval.title,
                                        _id: catval._id,
                                        image: catval.image,
                                        title: catval.title,
                                      }))
                                    : []
                                }
                                isMulti={false}
                              />
                            </div>
                          </div>
                          {(currentUser?.userType == Admin ||
                            currentUser?.userType == AssistAdmin) && (
                            <div className="d-flex align-items-center col-w-50 justify-content-between uw-common-p-5">
                              <label htmlFor="" className="fs-16 fw-500">
                                Filter by District
                              </label>
                              <div className="ms-6">
                                <CustomSelect
                                  backgroundColor="#ffff"
                                  minHeight="40px"
                                  isLoading={fetchLoader}
                                  closeMenuOnSelect={false}
                                  isSearchable={true}
                                  isClearable={true}
                                  options={
                                    districts && districts.length
                                      ? districts.map((val: any) => ({
                                          label: (
                                            <span className="fs-16 fw-600 text-black mb-0">
                                              {val.name}
                                            </span>
                                          ),
                                          value: val._id,
                                          id: val._id,
                                          title: val.name,
                                        }))
                                      : []
                                  }
                                  text="district selected"
                                  hideSelectedOptions={true}
                                  onChange={(event: any) => {
                                    if (event) {
                                      handleDistrict(event.id);
                                    } else {
                                      handleDistrict(null);
                                    }
                                  }}
                                  isMulti={false}
                                />
                              </div>
                            </div>
                          )}
                          <div className="d-flex align-items-center col-w-50 justify-content-between  uw-common-p-5">
                            <label htmlFor="" className="fs-16 fw-500">
                              Filter customer
                            </label>
                            <div className="col-w-60 ms-13">
                              <CustomSelect
                                id="customerFilter"
                                backgroundColor="#ffff"
                                default={customerStateJSON.find(
                                  (option) => option?.value === customerState
                                )}
                                options={customerStateJSON}
                                onChange={(event: any) =>
                                  handleFilter(event.value)
                                }
                                isSearchable={false}
                                isMulti={false}
                              />
                            </div>
                          </div>
                          <div className="d-flex align-items-center justify-content-between col-w-50 uw-common-p-5">
                            <label htmlFor="" className="fs-16 fw-500">
                              Filter by Customer Type
                            </label>
                            <div className="col-w-60 ms-6">
                              <CustomSelect
                                id="customerTypeFilter"
                                backgroundColor="#ffff"
                                options={customerTypeOptions}
                                onChange={(event: any) =>
                                  handleCustomerTypeSelect(event)
                                }
                                isSearchable={false}
                                isClearable={true}
                                isMulti={false}
                              />
                            </div>
                          </div>
                          <div className="d-flex align-items-center justify-content-between col-w-50 uw-common-p-5">
                            <label htmlFor="" className="fs-16 fw-500">
                              Filter by dates
                            </label>
                            <div className="col-w-60 ms-6">
                              <CustomDatePicker
                                className="form-control bg-white fs-16 fw-bold text-dark"
                                onChange={handleDateChange}
                                selectsRange={true}
                                startDate={startDate}
                                endDate={endDate}
                                dateFormat="dd/MM/yyyy"
                                showFullMonthYearPicker={true}
                                maxDate={new Date()}
                                isClearable={true}
                                inputTextBG="bg-white"
                              />
                            </div>
                          </div>
                        </Col>
                      </Card.Body>
                    </Card>

                    <Card className="border">
                      <Card.Body>
                        <div className="table-responsive">
                          <table className="table table-rounded table-row-bordered align-middle gy-4 mb-0">
                            <thead>
                              <tr className="fw-bold fs-16 fw-600 text-dark border-bottom align-middle">
                                <th className="col-w-25">Business</th>
                                <th className="col-w-10">Customer</th>
                                <th className="col-w-10" title="Business type">B-type</th>
                                <th className="col-w-10" title="Customer type">C-type</th>
                                <th className="col-w-10">Registered</th>
                                <th className="col-w-10">Order(s)</th>
                                <th className="col-w-10">Value</th>
                                <th className="col-w-5">Blocked</th>
                                <th className="col-w-10 text-end"></th>
                              </tr>
                            </thead>
                            <tbody>
                              {fetchTableLoading || loading ? (
                                <tr>
                                  <td colSpan={9}>
                                    <div className="w-100 d-flex justify-content-center">
                                      <Loader
                                        loading={
                                          fetchTableLoading ||
                                          fetchLoading ||
                                          loading
                                        }
                                      />
                                    </div>
                                  </td>
                                </tr>
                              ) : customers.length ? (
                                customers.map(
                                  (customVal: any, customIndex: number) => (
                                    <tr key={customIndex}>
                                      <td>
                                        <div className="d-flex align-items-center flex-row">
                                          <div className="symbol symbol-50px border position-relative">
                                            {customVal.regCompleted ||
                                            customerState ===
                                              NotRegistered ? null : (
                                              <div className="position-absolute start-0 bottom-0 m-n1">
                                                <OverlayTrigger
                                                  trigger={['hover', 'focus']}
                                                  placement="bottom"
                                                  overlay={popover}
                                                >
                                                  <img
                                                    className="error-icon"
                                                    src={errorDanger}
                                                    alt=""
                                                  />
                                                </OverlayTrigger>
                                              </div>
                                            )}
                                            <ImageWithFallback
                                              className="img-fluid border-r8px object-fit-contain"
                                              src={
                                                customVal.business.image
                                                  ? customVal.business.image
                                                  : b1
                                              }
                                              alt=""
                                              fallbackSrc={b1}
                                              noDefaultSize
                                            />
                                          </div>
                                          <span className="fs-15 fw-600 ms-3">
                                            {customVal.business.name
                                              ? customVal.business.name
                                              : '-'}
                                            {/* Email and phone under customer name */}
                                            <br />
                                            <span className="fs-13 fw-400 text-muted">
                                              {customVal.email || '-'}
                                            </span>
                                            <br />
                                            <span className="fs-13 fw-400 text-muted">
                                              {customVal.phoneCountry || ''}{' '}
                                              {customVal.phone || '-'}
                                            </span>
                                          </span>
                                        </div>
                                      </td>
                                      <td>
                                        <span className="fs-15 fw-500">
                                          {customVal.name
                                            ? customVal.name
                                            : '-'}
                                        </span>
                                      </td>
                                      <td>
                                        <span className="fs-15 fw-600">
                                          {customVal.business.types.length
                                            ? customVal.business.types[0]
                                                .reference.title
                                            : '-'}
                                        </span>
                                      </td>
                                      <td>
                                        <span className="fs-15 fw-500">
                                          {customVal?.isPharmacyVendor
                                            ? 'Pharmacy'
                                            : 'Utrade'}
                                        </span>
                                      </td>
                                      <td>
                                        <span className="fs-15 fw-500">
                                          {Method.convertDateToDDMMYYYY(
                                            customVal.createdAt
                                          )}
                                        </span>
                                      </td>
                                      <td>
                                        <span className="fs-15 fw-500 ms-3">
                                          {Method.getGeneralizedAmount(
                                            customVal?.analytics?.length &&
                                              customVal.analytics[0]
                                                ?.totalOrders
                                              ? customVal.analytics[0]
                                                  .totalOrders
                                              : 0
                                          )}
                                        </span>
                                      </td>
                                      <td>
                                        <span className="fs-15 fw-500">
                                          TSh{' '}
                                          {Method.getGeneralizedAmount(
                                            customVal?.analytics?.length &&
                                              customVal.analytics[0]
                                                ?.totalOrderValue
                                              ? customVal.analytics[0]
                                                  .totalOrderValue
                                              : 0
                                          )}
                                        </span>
                                      </td>
                                      <td className="fs-15 fw-500">
                                        {!Method.hasPermission(
                                          AllCustomers,
                                          Edit,
                                          currentUser
                                        ) ? (
                                          <OverlayTrigger
                                            placement="top"
                                            overlay={
                                              <Tooltip
                                                style={{
                                                  backgroundColor: 'white',
                                                }}
                                              >
                                                <span className="text-white">
                                                  {' '}
                                                  You do not have sufficient
                                                  permission to access this
                                                  module
                                                </span>
                                              </Tooltip>
                                            }
                                          >
                                            <label className="form-check form-switch form-check-custom form-check-solid justify-content-center">
                                              <input
                                                className="form-check-input form-check-success w-50px h-30px"
                                                type="checkbox"
                                                name="notifications"
                                                checked={customVal.blocked}
                                                disabled={true}
                                              />
                                            </label>
                                          </OverlayTrigger>
                                        ) : (
                                          <>
                                            <label className="form-check form-switch form-check-custom form-check-solid justify-content-center">
                                              <input
                                                className="form-check-input form-check-success w-50px h-30px"
                                                type="checkbox"
                                                name="notifications"
                                                checked={customVal.blocked}
                                                onChange={() =>
                                                  handleStatus(
                                                    customVal._id,
                                                    customVal.blocked,
                                                    customIndex
                                                  )
                                                }
                                              />
                                            </label>
                                          </>
                                        )}
                                      </td>
                                      <td>
                                        <div className="text-end">
                                          {Method.hasPermission(
                                            AllCustomers,
                                            View,
                                            currentUser
                                          ) ? (
                                            <Button
                                              className="uw-btn uw-btn-primary uw-btn-icon uw-btn-sm"
                                              aria-label="View customer details"
                                              data-analytics-event="customers_view_customers_view_details_clicked"
                                              onClick={() => {
                                                handleCustomerProfile(
                                                  customVal._id
                                                );
                                              }}
                                            >
                                              <span className="uw-btn-icon-content">
                                                {svg.eye}
                                              </span>
                                            </Button>
                                          ) : null}
                                        </div>
                                      </td>
                                    </tr>
                                  )
                                )
                              ) : (
                                <tr>
                                  <td colSpan={9}>
                                    <div className="w-100 fs-15 fw-bold d-flex justify-content-center">
                                      No Data found
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </Card.Body>
                    </Card>

                    {!loading && customers.length ? (
                      <Pagination
                        totalRecords={totalRecords}
                        currentPage={page}
                        handleCurrentPage={(event: any) => {
                          handleCurrentPage(event);
                        }}
                        handleNextPage={(event: any) => {
                          handleNextPage(event);
                        }}
                        handlePreviousPage={(event: any) => {
                          handlePreviousPage(event);
                        }}
                        pageLimit={pageLimit}
                        handlePageLimit={(event: any) => {
                          handlePageLimit(event);
                        }}
                      />
                    ) : null}
                  </>
                ) : null}
              </>
            ) : (
              <div className="d-flex justify-content-center">
                <Loader loading={fetchLoading} />
              </div>
            )}
          </Col>
        </Row>
      </div>
    </>
  );
};
export default ViewCustomers;
